import React, { useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { ocrAPI, categoriesAPI, accountsAPI } from '../lib/api';
import { formatCurrency, cn } from '../lib/utils';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  Camera, Upload, RefreshCw, Check, X, AlertCircle,
  CalendarIcon, Receipt, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const ScanReceiptDialog = ({ open, onOpenChange, onSuccess }) => {
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const [step, setStep] = useState('upload'); // upload, scanning, review, confirm
  const [loading, setLoading] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [categories, setCategories] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [previewUrl, setPreviewUrl] = useState(null);

  const [form, setForm] = useState({
    merchant: '',
    date: new Date(),
    amount: '',
    category_id: '',
    account_id: '',
    description: '',
    ocr_raw_text: ''
  });

  React.useEffect(() => {
    if (open) {
      loadData();
      resetState();
    }
  }, [open]);

  const loadData = async () => {
    try {
      const [catRes, accRes] = await Promise.all([
        categoriesAPI.getAll(),
        accountsAPI.getAll()
      ]);
      setCategories(catRes.data);
      setAccounts(accRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const resetState = () => {
    setStep('upload');
    setScanResult(null);
    setPreviewUrl(null);
    setForm({
      merchant: '',
      date: new Date(),
      amount: '',
      category_id: '',
      account_id: '',
      description: '',
      ocr_raw_text: ''
    });
  };

  const handleFileSelect = async (file) => {
    if (!file) return;

    // Create preview
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);

    // Start scanning
    setStep('scanning');
    setLoading(true);

    try {
      const response = await ocrAPI.scanReceipt(file);
      const result = response.data;
      setScanResult(result);

      // Pre-fill form with scanned data
      setForm({
        merchant: result.merchant || '',
        date: result.date ? new Date(result.date) : new Date(),
        amount: result.total_amount?.toString() || '',
        category_id: result.suggested_category_id || '',
        account_id: accounts[0]?.id || '',
        description: result.merchant || '',
        ocr_raw_text: result.raw_text || ''
      });

      setStep('review');

      if (result.confidence >= 0.7) {
        toast.success('Ticket analizado correctamente');
      } else if (result.confidence === 0.0) {
        toast.info('Modo manual activado', { description: 'Introduce los datos de tu gasto' });
      } else {
        toast.warning('Revisa los datos extraídos', { description: 'La calidad de la imagen podría ser baja' });
      }
    } catch (error) {
      toast.error('Error de conexión', { description: 'Puedes continuar manualmente' });
      // En vez de volver atrs, permitimos que el usuario lo intente manual.
      setScanResult({ confidence: 0.0 });
      setStep('review');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!form.amount || !form.category_id || !form.account_id) {
      toast.error('Completa los campos obligatorios');
      return;
    }

    setLoading(true);

    try {
      await ocrAPI.confirmReceipt({
        merchant: form.merchant,
        date: form.date.toISOString().split('T')[0],
        amount: parseFloat(form.amount),
        category_id: form.category_id,
        account_id: form.account_id,
        description: form.description || form.merchant,
        ocr_raw_text: form.ocr_raw_text
      });

      toast.success('Gasto registrado correctamente');
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="scan-receipt-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Receipt className="w-5 h-5 text-emerald-900" />
            Escanear Gasto
          </DialogTitle>
        </DialogHeader>

        {/* Step: Upload */}
        {step === 'upload' && (
          <div className="space-y-6 py-4">
            <p className="text-sm text-slate-500 text-center">
              Sube una foto de tu ticket o factura y extraeremos los datos automáticamente
            </p>

            <div className="grid grid-cols-2 gap-4">
              {/* Camera button (mobile) */}
              <button
                onClick={() => cameraInputRef.current?.click()}
                className="flex flex-col items-center justify-center p-8 border-2 border-dashed border-slate-200 rounded-sm hover:border-emerald-900 hover:bg-emerald-50 transition-colors"
                data-testid="camera-btn"
              >
                <Camera className="w-10 h-10 text-emerald-900 mb-3" strokeWidth={1.5} />
                <span className="text-sm font-medium text-slate-700">Tomar Foto</span>
                <span className="text-xs text-slate-400 mt-1">Cámara</span>
              </button>
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={(e) => handleFileSelect(e.target.files?.[0])}
                className="hidden"
              />

              {/* Upload button */}
              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex flex-col items-center justify-center p-8 border-2 border-dashed border-slate-200 rounded-sm hover:border-emerald-900 hover:bg-emerald-50 transition-colors"
                data-testid="upload-btn"
              >
                <Upload className="w-10 h-10 text-emerald-900 mb-3" strokeWidth={1.5} />
                <span className="text-sm font-medium text-slate-700">Subir Archivo</span>
                <span className="text-xs text-slate-400 mt-1">JPG, PNG, PDF</span>
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,.pdf"
                onChange={(e) => handleFileSelect(e.target.files?.[0])}
                className="hidden"
              />
            </div>

            <p className="text-xs text-slate-400 text-center">
              La imagen se analiza con IA para extraer comercio, fecha e importe
            </p>
          </div>
        )}

        {/* Step: Scanning */}
        {step === 'scanning' && (
          <div className="flex flex-col items-center justify-center py-12">
            {previewUrl && (
              <div className="w-32 h-32 mb-6 rounded-sm overflow-hidden border border-slate-200">
                <img src={previewUrl} alt="Ticket" className="w-full h-full object-cover" />
              </div>
            )}
            <RefreshCw className="w-10 h-10 text-emerald-900 animate-spin mb-4" />
            <p className="text-sm text-slate-600 font-medium">Analizando ticket...</p>
            <p className="text-xs text-slate-400 mt-1">Extrayendo datos con IA</p>
          </div>
        )}

        {/* Step: Review */}
        {step === 'review' && (
          <div className="space-y-4 py-2">
            {/* Confidence indicator / Fallback state */}
            {scanResult && (
              <div className={cn(
                "flex items-center gap-2 p-3 rounded-sm border",
                scanResult.confidence === 0.0
                  ? "bg-slate-50 border-slate-200 text-slate-600"
                  : scanResult.confidence >= 0.7
                    ? "bg-emerald-50 border-emerald-100 text-emerald-700"
                    : "bg-orange-50 border-orange-100 text-orange-700"
              )}>
                {scanResult.confidence === 0.0 ? (
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                ) : scanResult.confidence >= 0.7 ? (
                  <Sparkles className="w-4 h-4 flex-shrink-0" />
                ) : (
                  <AlertCircle className="w-4 h-4 flex-shrink-0 text-orange-600" />
                )}
                <span className="text-xs font-medium">
                  {scanResult.confidence === 0.0
                    ? "Modo manual: Introduce los datos del gasto"
                    : scanResult.confidence >= 0.7
                      ? "Datos extraídos con alta confianza"
                      : "Confianza parcial: Revisa los campos con atención"
                  }
                </span>
              </div>
            )}

            {/* Preview image */}
            {previewUrl && (
              <div className="flex justify-center">
                <div className="w-24 h-24 rounded-sm overflow-hidden border border-slate-200">
                  <img src={previewUrl} alt="Ticket" className="w-full h-full object-cover" />
                </div>
              </div>
            )}

            {/* Form */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-2">
                <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">
                  Comercio
                </Label>
                <Input
                  value={form.merchant}
                  onChange={(e) => setForm(prev => ({ ...prev, merchant: e.target.value }))}
                  className="rounded-sm"
                  placeholder="Nombre del comercio"
                  data-testid="merchant-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">
                  Importe *
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">€</span>
                  <Input
                    type="number"
                    step="0.01"
                    value={form.amount}
                    onChange={(e) => setForm(prev => ({ ...prev, amount: e.target.value }))}
                    className="pl-8 font-mono rounded-sm"
                    required
                    data-testid="amount-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">
                  Fecha
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left rounded-sm">
                      <CalendarIcon size={16} className="mr-2 text-slate-400" />
                      {format(form.date, 'dd/MM/yyyy')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={form.date}
                      onSelect={(date) => date && setForm(prev => ({ ...prev, date }))}
                      locale={es}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">
                  Categoría *
                </Label>
                <Select value={form.category_id} onValueChange={(v) => setForm(prev => ({ ...prev, category_id: v }))}>
                  <SelectTrigger className="rounded-sm" data-testid="category-select">
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">
                  Cuenta *
                </Label>
                <Select value={form.account_id} onValueChange={(v) => setForm(prev => ({ ...prev, account_id: v }))}>
                  <SelectTrigger className="rounded-sm" data-testid="account-select">
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    {accounts.map(acc => (
                      <SelectItem key={acc.id} value={acc.id}>{acc.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="col-span-2 space-y-2">
                <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">
                  Descripción
                </Label>
                <Input
                  value={form.description}
                  onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                  className="rounded-sm"
                  placeholder="Descripción del gasto"
                />
              </div>
            </div>

            {/* Items detected */}
            {scanResult?.items && scanResult.items.length > 0 && (
              <div className="p-3 bg-slate-50 border border-slate-100 rounded-sm">
                <p className="text-xs uppercase tracking-widest font-semibold text-slate-400 mb-2">
                  Artículos detectados
                </p>
                <div className="space-y-1">
                  {scanResult.items.slice(0, 5).map((item, i) => (
                    <div key={i} className="flex justify-between text-sm">
                      <span className="text-slate-600 truncate">{item.name}</span>
                      <span className="font-mono text-slate-900">€{item.price?.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Legal / AI Disclaimer (Obligatorio) */}
            <div className="mt-4 pt-4 border-t border-slate-100 flex items-start gap-2">
              <span className="text-slate-400 text-xs mt-0.5">⚠️</span>
              <p className="text-[11px] text-slate-500 leading-snug">
                Datos sugeridos mediante IA. Revísalos antes de confirmar. La IA puede cometer errores.
              </p>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          {step === 'review' && (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={resetState}
                className="rounded-sm"
              >
                <X size={16} className="mr-2" />
                Cancelar
              </Button>
              <Button
                onClick={handleConfirm}
                disabled={loading}
                className="bg-emerald-900 hover:bg-emerald-800 rounded-sm"
                data-testid="confirm-btn"
              >
                {loading ? (
                  <RefreshCw size={16} className="mr-2 animate-spin" />
                ) : (
                  <Check size={16} className="mr-2" />
                )}
                Confirmar Gasto
              </Button>
            </>
          )}
          {step === 'upload' && (
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="rounded-sm"
            >
              Cancelar
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ScanReceiptDialog;
